import type { InvitationValidationResponse } from '@bingads-webui-campaign-react/accept-invitation-api';

// English indefinite article for the role name — "an" before a vowel sound, else "a". The role name
// is the server-provided (English) display name, so the article is derived from it directly. e.g.
// "Advertiser Campaign Manager" -> "an", "Campaign Manager" -> "a".
const getRoleArticle = (roleName: string): string => (/^[aeiou]/i.test(roleName.trim()) ? 'an' : 'a');

// Builds the personalized invitation summary line shown under the landing-page heading, e.g.
//   "John Doe invited you to be a Campaign Manager for Acme Corp, Poodle Corp and 3 more advertising accounts."
// from the Validate response's sender / role / account fields. Two shapes:
//   - customer-level access (`isCustomerLevelAccess === true`): "… for {customerName}."
//   - account-level access: "… for {a, b, c[ and N more]} advertising accounts.", where the trailing
//     "and N more" is added only when the true total (`accountCount`) exceeds the named accounts.
// Returns null when the sender/role (or the target name) is unavailable, so the caller can omit the
// line and fall back to the heading alone.
export const getInvitationSummaryText = (
  i18n: any,
  validation?: InvitationValidationResponse | null
): string | null => {
  const senderName = validation?.senderName;
  const roleName = validation?.roleName;
  if (!senderName || !roleName) {
    return null;
  }
  const article = getRoleArticle(roleName);

  if (validation?.isCustomerLevelAccess) {
    const customerName = validation?.customerName;
    if (!customerName) {
      return null;
    }
    return i18n.getString(
      _TL_('{{senderName}} invited you to be {{article}} {{roleName}} for {{customerName}}.'),
      {
        senderName, article, roleName, customerName,
      }
    );
  }

  const accounts = Array.isArray(validation?.accounts) ? validation.accounts : [];
  if (accounts.length === 0) {
    return null;
  }
  const accountsText = accounts.map(account => account.name).join(', ');
  const accountCount = typeof validation?.accountCount === 'number' ? validation.accountCount : accounts.length;
  const remaining = accountCount - accounts.length;
  if (remaining > 0) {
    return i18n.getString(
      _TL_('{{senderName}} invited you to be {{article}} {{roleName}} for {{accounts}} and {{remaining}} more advertising accounts.'),
      {
        senderName, article, roleName, accounts: accountsText, remaining,
      }
    );
  }
  return i18n.getString(
    _TL_('{{senderName}} invited you to be {{article}} {{roleName}} for {{accounts}} advertising accounts.'),
    {
      senderName, article, roleName, accounts: accountsText,
    }
  );
};
